import { createColumnHelper } from '@tanstack/react-table';
import { pipe } from 'fp-ts/function';
import { array } from 'fp-ts';
import { values } from '@mews/fp';
import { PortfolioRateOverview, PortfolioRateType } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { LocalizedEnum, LocalizedText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import { ColumnFilterType, Tag } from '@optimus-web/core';
import { LocalizedTextTag } from '@mews-commander/ui';
import { RowActions } from '../../Rates/RowActions';

const columnHelper = createColumnHelper<PortfolioRateOverview>();

const statusOptions = {
    undefined: <LocalizedText textKey={TextKey.All} />,
    true: <LocalizedText textKey={TextKey.Enabled} />,
    false: <LocalizedText textKey={TextKey.Disabled} />,
};
const getStatusOptions = () => statusOptions;

export const columns = [
    columnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.Name} />,
    }),
    columnHelper.accessor('isBaseRate', {
        header: () => <LocalizedText textKey={TextKey.Type} />,
        cell: (props) => {
            const value = props.getValue();
            return (
                <Tag variant={value ? 'primary' : 'basicSubtle'} outline>
                    <LocalizedText textKey={value ? TextKey.BaseRate : TextKey.DependentRate} />
                </Tag>
            );
        },
    }),
    columnHelper.accessor('type', {
        header: () => <LocalizedText textKey={TextKey.RateVisibility} />,
        cell: (props) => {
            const value = props.getValue();
            return (
                <Tag variant={value === PortfolioRateType.Public ? 'primary' : 'basicSubtle'} outline>
                    <LocalizedEnum prefix="RateType" value={value} />
                </Tag>
            );
        },
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.MultiSelect,
            getMultiSelectOptions: () =>
                pipe(
                    PortfolioRateType,
                    values,
                    array.filter((value) => value !== PortfolioRateType.AvailabilityBlock),
                    array.map((value) => ({
                        value,
                        label: <LocalizedEnum prefix="RateType" value={value} />,
                    }))
                ),
        },
    }),
    columnHelper.accessor('shortName', {
        header: () => <LocalizedText textKey={TextKey.ShortName} />,
    }),
    columnHelper.accessor('externalName', {
        header: () => <LocalizedText textKey={TextKey.ExternalName} />,
    }),
    columnHelper.accessor('key', {
        header: () => <LocalizedText textKey={TextKey.RateKey} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('accountingCategoryName', {
        header: () => <LocalizedText textKey={TextKey.AccountingCategory} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('segmentName', {
        header: () => <LocalizedText textKey={TextKey.PortfolioBusinessSegment} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableColumnFilter: true,
        enableSorting: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    columnHelper.accessor('isDefault', {
        header: () => <LocalizedText textKey={TextKey.Default} />,
        cell: (props) => {
            const value = props.getValue();
            return (
                <LocalizedTextTag
                    textKey={value ? TextKey.Yes : TextKey.No}
                    variant={value ? 'primary' : 'basicSubtle'}
                    outline
                />
            );
        },
        enableSorting: true,
    }),
    columnHelper.accessor('isEnabled', {
        header: () => <LocalizedText textKey={TextKey.Status} />,
        cell: (props) => {
            const value = props.getValue();
            return (
                <Tag variant={value ? 'success' : 'danger'} outline>
                    <LocalizedText textKey={value ? TextKey.Enabled : TextKey.Disabled} />
                </Tag>
            );
        },
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.Bool,
            getBoolOptions: getStatusOptions,
        },
    }),
    columnHelper.display({
        id: 'actions',
        header: '',
        meta: { align: 'end' },
        cell: (props) => <RowActions rate={props.row.original} setRowSelection={props.table.setRowSelection} />,
    }),
];
